import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaCheck, FaDownload, FaPencilAlt, FaPrint } from "react-icons/fa";
import {
  createPdfFromFinanceInvoiceDraft,
  downloadPdfBlob,
  getPdfBlobFromFinanceInvoiceDraft,
} from "../GeneratePDF/logic";
import defaultBrandLogo from "../../assets/logo1.png";
import {
  formatInvoiceMoney,
  buildComputedLineItem,
  formatCommissionPreviewGstRate,
  formatLineItemTaxRateDisplay,
  getLineDisplayTotalAmount,
  resolveExTaxFooterTotals,
  resolvePreviewTaxLabel,
} from "../../utilis/invoiceLineItemHelpers";
import { buildInvoiceClientPartyFields } from "../../utilis/applicationCostHelpers";
import {
  resolveInvoiceDisplayTitle,
  getBranchDisplayName,
  getBranchAddress,
  getBranchCountry,
  formatBranchContactNumber,
  getBranchEmail,
  parsePaymentAdviceContent,
  parseTermsConditionsContent,
  buildBranchFromExtraLines,
  parseBrandingCompanyDetails,
  resolveInvoiceDocumentAmountDue,
  resolveInvoicePreviewTotals,
  buildClassicInvoiceBillToLines,
  buildClassicInvoiceCompanyContact,
  buildInvoiceLessPaidLabelParts,
  formatInvoicePreviewMetaDate,
  prepareFromPartyFields,
  filterPaymentAdviceBlocks,
} from "../../utilis/invoicePreviewHelpers";
import {
  normalizeInvoiceDisplayOptions,
  toCommissionPartyPrintOptions,
} from "../reports/commissions/commissionInvoiceDisplayOptions";
import {
  buildCommissionInvoiceBillToLines,
  buildCommissionInvoicePartyFields,
} from "../reports/commissions/commissionBillToUtils";
import {
  resolveInvoiceLogoUrl,
  getInvoiceSettingTradingName,
  getInvoiceSettingAddressLines,
  getInvoiceSettingPhoneLines,
  getInvoiceSettingEmail,
  formatInvoiceSettingWebsite,
} from "../../utilis/invoiceSettingHelpers";

function formatPreviewDate(value) {
  if (!value) return "—";

  const date = new Date(`${value}T00:00:00`);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function PreviewMetaRow({ label, value }) {
  return (
    <div className="invoice-xero-preview__meta-row">
      <span className="invoice-xero-preview__meta-label">{label}</span>
      <span className="invoice-xero-preview__meta-value">{value || "—"}</span>
    </div>
  );
}

function ClassicMetaRow({ label, value, valueWrap = false }) {
  if (!String(label || "").trim()) {
    return (
      <div className="invoice-xero-preview__classic-meta-row invoice-xero-preview__classic-meta-row--value-only">
        <span
          className={`invoice-xero-preview__classic-meta-value${
            valueWrap ? " invoice-xero-preview__classic-meta-value--wrap" : ""
          }`}
        >
          {value || "—"}
        </span>
      </div>
    );
  }

  return (
    <div className="invoice-xero-preview__classic-meta-row">
      <span className="invoice-xero-preview__classic-meta-label">{label}</span>
      <span
        className={`invoice-xero-preview__classic-meta-value${
          valueWrap ? " invoice-xero-preview__classic-meta-value--wrap" : ""
        }`}
      >
        {value || "—"}
      </span>
    </div>
  );
}

function PreviewPartyLine({ children }) {
  if (!children) return null;

  return <div className="invoice-xero-preview__party-line">{children}</div>;
}

function PreviewPartyField({ label, value }) {
  const text = String(value || "").trim();

  if (!text) return null;

  if (!label) {
    return <div className="invoice-xero-preview__party-line">{text}</div>;
  }

  return (
    <div className="invoice-xero-preview__party-field">
      <span className="invoice-xero-preview__party-field-label">{label}: </span>
      <span className="invoice-xero-preview__party-field-value">{text}</span>
    </div>
  );
}

function PreviewInlineEditable({
  value,
  editable = false,
  readOnly = false,
  onSave,
  className = "invoice-xero-preview__party-name",
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value || ""));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(String(value || ""));
  }, [value, editing]);

  if (!editable || readOnly) {
    return value ? <div className={className}>{value}</div> : null;
  }

  if (!editing) {
    return (
      <div className={`${className} invoice-xero-preview__editable-row`}>
        <span className="invoice-xero-preview__editable-text" title={value || ""}>
          {value || "—"}
        </span>
        <button
          type="button"
          className="invoice-xero-preview__ui-only invoice-xero-preview__icon-btn"
          aria-label="Edit"
          onClick={() => setEditing(true)}
        >
          <FaPencilAlt size={11} />
        </button>
      </div>
    );
  }

  return (
    <div className="invoice-xero-preview__editable-row invoice-xero-preview__editable-row--edit">
      <input
        type="text"
        className="invoice-xero-preview__inline-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={saving}
      />
      <button
        type="button"
        className="invoice-xero-preview__ui-only invoice-xero-preview__icon-btn invoice-xero-preview__icon-btn--save"
        aria-label="Save"
        disabled={saving}
        onClick={async () => {
          try {
            setSaving(true);
            await onSave?.(draft);
            setEditing(false);
          } catch {
            /* caller toasts */
          } finally {
            setSaving(false);
          }
        }}
      >
        <FaCheck size={11} />
      </button>
    </div>
  );
}

function PartyColumnPlaceholder() {
  return (
    <div
      className="invoice-xero-preview__party-block invoice-xero-preview__party-block--placeholder"
      aria-hidden="true"
    />
  );
}

function PreviewPartyBlock({
  title,
  name,
  lines = [],
  fields = null,
  nameEditable = false,
  nameEditReadOnly = false,
  onSaveName,
  showName = true,
}) {
  const visibleFields = Array.isArray(fields) ? fields : null;
  const visibleLines = lines.filter(Boolean);

  return (
    <div className="invoice-xero-preview__party-block">
      <div className="invoice-xero-preview__party-label">{title}</div>

      {showName && name ? (
        nameEditable ? (
          <PreviewInlineEditable
            value={name}
            editable
            readOnly={nameEditReadOnly}
            onSave={onSaveName}
          />
        ) : (
          <div className="invoice-xero-preview__party-name">{name}</div>
        )
      ) : null}

      {visibleFields
        ? visibleFields.map((field, index) => (
            <PreviewPartyField
              key={`${title}-field-${index}`}
              label={field.label}
              value={field.value}
            />
          ))
        : visibleLines.map((line, index) => (
            <PreviewPartyLine key={`${title}-${index}`}>
              {line}
            </PreviewPartyLine>
          ))}
    </div>
  );
}

function PaymentAdviceSection({ content, footerOptions = null }) {
  const blocks = filterPaymentAdviceBlocks(
    parsePaymentAdviceContent(content),
    footerOptions,
  );

  if (!blocks.length) return null;

  return (
    <div className="invoice-xero-preview__footer-body invoice-xero-preview__footer-body--payment">
      {blocks.map((block, index) => {
        if (block.type === "field") {
          const label = block.label.endsWith(":")
            ? block.label
            : `${block.label}:`;
          const isLongLabel = label.length > 32;

          if (isLongLabel) {
            return (
              <div
                key={`field-${index}`}
                className="invoice-xero-preview__footer-field invoice-xero-preview__footer-field--stacked"
              >
                <div className="invoice-xero-preview__footer-field-label">
                  {label}
                </div>
                <div className="invoice-xero-preview__footer-field-value">
                  {block.value || "—"}
                </div>
              </div>
            );
          }

          return (
            <div
              key={`field-${index}`}
              className="invoice-xero-preview__footer-field"
            >
              <span className="invoice-xero-preview__footer-field-label">
                {label}
              </span>
              <span className="invoice-xero-preview__footer-field-value">
                {block.value || "—"}
              </span>
            </div>
          );
        }

        return (
          <div
            key={`line-${index}`}
            className="invoice-xero-preview__footer-line"
          >
            {block.text}
          </div>
        );
      })}
    </div>
  );
}

function TermsConditionsSection({ content }) {
  const lines = parseTermsConditionsContent(content);

  if (!lines.length) return null;

  return (
    <div className="invoice-xero-preview__footer-body">
      {lines.map((line, index) => {
        const text = line.replace(/^\d+\.\s*/, "").trim();

        const display = text.startsWith("*") ? text : `* ${text}`;

        return (
          <div
            key={`term-${index}`}
            className="invoice-xero-preview__footer-line"
          >
            {display}
          </div>
        );
      })}
    </div>
  );
}

function PreviewLineDescription({ row }) {
  const description = String(row.description || "—");
  const descriptionLines = description.split(/\r?\n/);

  return (
    <div className="invoice-xero-preview__line-desc">
      <div className="invoice-xero-preview__line-desc-text">
        {descriptionLines.map((line, index) => (
          <div key={`desc-line-${index}`}>{line || "\u00A0"}</div>
        ))}
      </div>
    </div>
  );
}

function buildInvoicePdfFilename(invoiceNumber) {
  const raw = String(invoiceNumber || "").trim();

  const base = raw || "invoice";

  const safe = base.replace(/[<>:"/\\|?*\u0000-\u001f]/g, "-");

  return safe.toLowerCase().endsWith(".pdf") ? safe : `${safe}.pdf`;
}

function PreviewTotalRow({
  label,
  value,
  variant = "",
  showDividerBefore = false,
}) {
  return (
    <div
      className={`invoice-xero-preview__total-row${
        variant ? ` invoice-xero-preview__total-row--${variant}` : ""
      }${showDividerBefore ? " invoice-xero-preview__total-row--divider" : ""}`}
    >
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function PreviewLessPaidRow({ paymentDate, value }) {
  const { prefix, detail } = buildInvoiceLessPaidLabelParts(paymentDate);

  return (
    <div className="invoice-xero-preview__total-row invoice-xero-preview__total-row--paid">
      <span className="invoice-xero-preview__less-paid-label">
        <span className="invoice-xero-preview__less-paid-prefix">{prefix}</span>
        <span className="invoice-xero-preview__less-paid-detail">{detail}</span>
      </span>
      <strong>{value}</strong>
    </div>
  );
}

function InvoiceXeroPreview({
  draft,
  onClose,
  showToolbar = true,
  embedded = false,
  autoPrint = false,
  captureRef = null,
  interactiveCommissionPreview = false,
  previewUiReadOnly = false,
  commissionProviderId = null,
  onSaveProviderName,
  onSaveAbn,
  invoiceSettingId = null,
}) {
  const sheetRef = useRef(null);

  const assignSheetRef = (node) => {
    sheetRef.current = node;
    if (captureRef && typeof captureRef === "object") {
      captureRef.current = node;
    }
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const resolvedLogo = useMemo(() => {
    const customLogo = resolveInvoiceLogoUrl(draft?.invoiceSetting?.c_logo);
    return customLogo || defaultBrandLogo;
  }, [draft?.invoiceSetting]);

  useEffect(() => {
    if (!autoPrint || !draft) return undefined;

    const timer = window.setTimeout(async () => {
      try {
        setDownloadingPdf(true);
        const blob = await getPdfBlobFromFinanceInvoiceDraft(draft);
        downloadPdfBlob(
          blob,
          buildInvoicePdfFilename(
            draft.invoiceNumber || draft.invoice_number,
          ),
        );
        onClose?.();
      } catch (error) {
        console.error("Failed to download invoice PDF", error);
        window.alert("Unable to download PDF. Please try again.");
      } finally {
        setDownloadingPdf(false);
      }
    }, 350);

    return () => window.clearTimeout(timer);
  }, [autoPrint, draft, onClose]);

  if (!draft) return null;

  const {
    documentType,
    brandingTitle,
    invoiceNumber,
    issueDate,
    dueDate,
    reference,
    clientProfile,
    currency,
    branding,
    branch,
    invoiceSetting,
    lineItems = [],
    gross_subtotal: grossSubtotal = 0,
    discount_total: discountTotal = 0,
    subtotal = 0,
    tax_amount: taxAmount = 0,
    inclusive_tax_amount: inclusiveTaxAmount = 0,
    exclusive_tax_amount: exclusiveTaxAmount = 0,
    total_amount: totalAmount = 0,
    paid_amount: paidAmount = 0,
    amount_due: amountDueValue,
    paymentDate = "",
  } = draft;

  const previewDisplay = draft?.previewDisplay || {};
  const displayOpts = normalizeInvoiceDisplayOptions(
    previewDisplay.invoiceDisplayOptions,
  );
  const showLogo = displayOpts.header.showLogo !== false;
  const showLineDiscount = previewDisplay.showLineDiscount !== false;
  const showDiscountTotal = previewDisplay.showDiscountTotal !== false;
  const useCommissionLineLayout = Boolean(previewDisplay.useCommissionLineLayout);
  const useExTaxLineLayout = Boolean(
    previewDisplay.useExTaxLineLayout || previewDisplay.useCommissionLineLayout,
  );

  const paidTotal = Number(paidAmount) || 0;

  const previewTotals = resolveInvoicePreviewTotals({
    gross_subtotal: grossSubtotal,
    subtotal,
    discount_total: discountTotal,
    total_amount: totalAmount,
    paid_amount: paidTotal,
    amount_due: amountDueValue,
    lineItems,
  });

  const {
    displaySubtotal: baseDisplaySubtotal,
    displayDiscount,
    hasDiscount,
    displayTotal: baseDisplayTotal,
    displayAmountDue: baseDisplayAmountDue,
    paidTotal: previewPaidTotal,
  } = previewTotals;

  let displaySubtotal = baseDisplaySubtotal;
  let displayTotal = baseDisplayTotal;
  let displayAmountDue = baseDisplayAmountDue;
  let taxLabel = "";
  let displayTaxAmount = 0;

  if (useExTaxLineLayout) {
    const computedItems = lineItems.map((row) =>
      buildComputedLineItem(row, row.tax_type),
    );
    const footerTotals = resolveExTaxFooterTotals(
      computedItems,
      draft?.taxType,
    );
    displaySubtotal = footerTotals.subtotalExTax;
    displayTotal = footerTotals.total;
    taxLabel = footerTotals.taxLabel;
    displayTaxAmount = footerTotals.gst;
    displayAmountDue = resolveInvoiceDocumentAmountDue({
      displayTotal,
      paidTotal: previewPaidTotal,
      explicitAmountDue: amountDueValue,
    });
  } else {
    const hasInclusiveTax = inclusiveTaxAmount > 0;
    const hasExclusiveTax = exclusiveTaxAmount > 0;
    taxLabel = resolvePreviewTaxLabel({
      lineItems,
      inclusiveTaxAmount,
      exclusiveTaxAmount,
      taxAmount,
      documentTaxType: draft?.taxType,
    });
    displayTaxAmount = hasExclusiveTax
      ? exclusiveTaxAmount
      : hasInclusiveTax
        ? inclusiveTaxAmount
        : Number(taxAmount) || 0;
  }

  const documentTitle =
    brandingTitle ||
    resolveInvoiceDisplayTitle({
      branding,
      documentType,
    });

  const companyName =
    getInvoiceSettingTradingName(invoiceSetting) || "Aptitude Migration";

  const branchName = getBranchDisplayName(branch);
  const branchAddress = getBranchAddress(branch);
  const branchCountry = getBranchCountry(branch);
  const branchPhone = formatBranchContactNumber(branch);
  const branchEmail = getBranchEmail(branch);

  const brandingFrom = parseBrandingCompanyDetails(branding?.contact_details);

  const hasBrandingFrom =
    Boolean(brandingFrom.name) || brandingFrom.lines.length > 0;

  const branchExtraLines = buildBranchFromExtraLines(branch, {
    includeBranchName: hasBrandingFrom,
  });

  const fromName = hasBrandingFrom
    ? brandingFrom.name || companyName
    : branchName || companyName || "—";

  const fromFields = hasBrandingFrom
    ? [
        ...brandingFrom.fields,
        ...branchExtraLines.map((line) => {
          const match = String(line).match(/^([^:]+):\s*(.*)$/);

          if (match) {
            return {
              label: match[1].trim(),
              value: match[2].trim(),
            };
          }

          return {
            label: "",
            value: line,
          };
        }),
      ]
    : [
        {
          label: "Phone",
          value:
            branchPhone ||
            getInvoiceSettingPhoneLines(invoiceSetting).join(" / "),
        },
        {
          label: "Email",
          value: branchEmail || getInvoiceSettingEmail(invoiceSetting),
        },
        {
          label: "Website",
          value: formatInvoiceSettingWebsite(invoiceSetting?.website),
        },
        {
          label: "Address",
          value: [
            branchAddress,
            branchCountry,
            ...(!branchAddress
              ? getInvoiceSettingAddressLines(invoiceSetting)
              : []),
          ]
            .filter(Boolean)
            .join(", "),
        },
        ...buildBranchFromExtraLines(branch, {
          includeBranchName: false,
        }).map((line) => {
          const match = String(line).match(/^([^:]+):\s*(.*)$/);

          if (match) {
            return {
              label: match[1].trim(),
              value: match[2].trim(),
            };
          }

          return {
            label: "",
            value: line,
          };
        }),
      ].filter((field) => String(field.value || "").trim());

  const visibleFromFields = prepareFromPartyFields(
    fromFields,
    displayOpts.from,
  );
  const fromLines = visibleFromFields.map((field) =>
    field.label ? `${field.label}: ${field.value}` : field.value,
  );

  const abn = String(invoiceSetting?.abn || "").trim();

  const commissionBillToPrintOptions =
    previewDisplay?.invoiceDisplayOptions
      ? toCommissionPartyPrintOptions(previewDisplay.invoiceDisplayOptions)
      : previewDisplay?.commissionBillToPrintOptions || null;

  const providerDisplayName = String(clientProfile?.fullName || "").trim();
  const canEditProviderName =
    interactiveCommissionPreview &&
    !previewUiReadOnly &&
    commissionProviderId &&
    typeof onSaveProviderName === "function";
  const canEditAbn =
    interactiveCommissionPreview &&
    !previewUiReadOnly &&
    invoiceSettingId &&
    typeof onSaveAbn === "function";

  const billToLines = useCommissionLineLayout
    ? commissionBillToPrintOptions
      ? buildCommissionInvoiceBillToLines(
          clientProfile,
          commissionBillToPrintOptions,
        )
      : buildClassicInvoiceBillToLines(clientProfile)
    : [];

  const companyContact = useCommissionLineLayout
    ? buildClassicInvoiceCompanyContact({
        invoiceSetting,
        branch,
        brandingFrom: hasBrandingFrom ? brandingFrom : null,
        companyName,
      })
    : { name: "", fieldRows: [] };

  const visibleLines = lineItems.filter(
    (row) => row.description?.trim() || row.unit_price > 0,
  );

  const formatAmount = (value) => formatInvoiceMoney(value, "", {});

  const handleDownloadPdf = async () => {
    if (!draft || downloadingPdf) return;

    try {
      setDownloadingPdf(true);
      await createPdfFromFinanceInvoiceDraft(
        draft,
        buildInvoicePdfFilename(invoiceNumber),
      );
    } catch (error) {
      console.error("Failed to download invoice PDF", error);

      window.alert("Unable to download PDF. Please try again.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  /*
   * IMPORTANT:
   *
   * The invoice sheet intentionally contains ONE complete
   * invoice document only.
   *
   * The previous implementation rendered:
   *   1. The invoice inside the masthead section
   *   2. Another complete invoice immediately afterward
   *
   * That caused the preview to show the invoice twice.
   *
   * The duplicate second invoice has been removed.
   */
  const sheet = (
    <div
      ref={assignSheetRef}
      className={`invoice-xero-preview__sheet${
        embedded ? " invoice-xero-preview__sheet--print-page" : ""
      }${useCommissionLineLayout ? " invoice-xero-preview__sheet--classic" : ""}`}
    >
      {useCommissionLineLayout ? (
        <>
          <div className="invoice-xero-preview__classic-top">
            <h1 className="invoice-xero-preview__classic-title">
              {documentTitle}
            </h1>
            <div
              className={`invoice-xero-preview__classic-logo-wrap${
                showLogo ? "" : " invoice-xero-preview__classic-logo-wrap--empty"
              }`}
              aria-hidden={!showLogo}
            >
              {showLogo ? (
                <img
                  src={resolvedLogo}
                  alt={companyName}
                  className="invoice-xero-preview__classic-logo"
                />
              ) : null}
            </div>
          </div>

          <div className="invoice-xero-preview__classic-info">
            <div className="invoice-xero-preview__classic-billto">
              <div className="invoice-xero-preview__classic-billto-label">To</div>
              {billToLines.length ? (
                billToLines.map((line, index) => (
                  <div
                    key={`billto-${index}`}
                    className="invoice-xero-preview__classic-billto-line"
                  >
                    {line}
                  </div>
                ))
              ) : (
                <div className="invoice-xero-preview__classic-billto-line">
                  —
                </div>
              )}
            </div>

            <div className="invoice-xero-preview__classic-meta">
              <ClassicMetaRow
                label="Invoice Date"
                value={formatPreviewDate(issueDate)}
              />
              <ClassicMetaRow
                label="Invoice Number"
                value={invoiceNumber}
              />
              <ClassicMetaRow label="Reference" value={reference} valueWrap />
              {abn ? <ClassicMetaRow label="ABN" value={abn} /> : null}
            </div>

            <div className="invoice-xero-preview__classic-company">
              {companyContact.name ? (
                <div className="invoice-xero-preview__classic-company-name">
                  {companyContact.name}
                </div>
              ) : null}
              {companyContact.fieldRows.map((row, index) => (
                <ClassicMetaRow
                  key={`company-field-${index}`}
                  label={row.label}
                  value={row.value}
                  valueWrap
                />
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="invoice-xero-preview__header-band">
          <div
            className={`invoice-xero-preview__header-brand${
              showLogo ? "" : " invoice-xero-preview__header-brand--empty"
            }`}
            aria-hidden={!showLogo}
          >
            {showLogo ? (
              <img
                src={resolvedLogo}
                alt={companyName}
                className="invoice-xero-preview__logo"
              />
            ) : null}
          </div>

          <div className="invoice-xero-preview__header-title-wrap">
            <h1 className="invoice-xero-preview__doc-title">{documentTitle}</h1>

            {invoiceNumber ? (
              <div className="invoice-xero-preview__header-meta">
                <span className="invoice-xero-preview__header-meta-label">
                  Invoice No.
                </span>
                <span className="invoice-xero-preview__header-meta-value">
                  {invoiceNumber}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      )}

      <div
        className={`invoice-xero-preview__body${
          useCommissionLineLayout ? " invoice-xero-preview__body--classic" : ""
        }`}
      >
        {!useCommissionLineLayout ? (
        <div className="invoice-xero-preview__parties">
          {displayOpts.to.showSection !== false ? (
            <PreviewPartyBlock
              title="To"
              name={
                commissionBillToPrintOptions &&
                displayOpts.to.showName !== false
                  ? providerDisplayName
                  : ""
              }
              showName={
                Boolean(commissionBillToPrintOptions) &&
                displayOpts.to.showName !== false
              }
              nameEditable={canEditProviderName}
              nameEditReadOnly={previewUiReadOnly}
              onSaveName={onSaveProviderName}
              fields={
                commissionBillToPrintOptions
                  ? buildCommissionInvoicePartyFields(
                      clientProfile,
                      commissionBillToPrintOptions,
                    ).filter((field) => field.label !== "Name")
                  : buildInvoiceClientPartyFields(clientProfile)
              }
            />
          ) : (
            <PartyColumnPlaceholder />
          )}

          {displayOpts.from.showSection !== false ? (
            <PreviewPartyBlock
              title="From"
              name={fromName}
              showName={displayOpts.from.showName !== false}
              fields={visibleFromFields}
              lines={fromLines}
            />
          ) : (
            <PartyColumnPlaceholder />
          )}

          {displayOpts.details.showSection !== false ? (
            <div className="invoice-xero-preview__party-block invoice-xero-preview__party-block--meta">
              <div className="invoice-xero-preview__party-label">
                Invoice details
              </div>

              {displayOpts.details.showIssueDate !== false ? (
                <PreviewMetaRow
                  label="Issue date"
                  value={formatPreviewDate(issueDate)}
                />
              ) : null}

              {displayOpts.details.showInvoiceNumber !== false ? (
                <PreviewMetaRow label="Invoice number" value={invoiceNumber} />
              ) : null}

              {displayOpts.details.showReference !== false ? (
                <PreviewMetaRow label="Reference" value={reference} />
              ) : null}

              {displayOpts.details.showDueDate !== false ? (
                <PreviewMetaRow
                  label="Due date"
                  value={formatPreviewDate(dueDate)}
                />
              ) : null}

              {previewPaidTotal > 0 &&
              paymentDate &&
              displayOpts.details.showPaymentDate !== false ? (
                <PreviewMetaRow
                  label="Payment date"
                  value={formatInvoicePreviewMetaDate(paymentDate)}
                />
              ) : null}

              {displayOpts.details.showAbn !== false ? (
                canEditAbn ? (
                  <div className="invoice-xero-preview__meta-row">
                    <span className="invoice-xero-preview__meta-label">ABN</span>
                    <PreviewInlineEditable
                      value={abn}
                      editable
                      readOnly={previewUiReadOnly}
                      className="invoice-xero-preview__meta-value invoice-xero-preview__meta-value--inline"
                      onSave={onSaveAbn}
                    />
                  </div>
                ) : abn ? (
                  <PreviewMetaRow label="ABN" value={abn} />
                ) : canEditAbn ? (
                  <div className="invoice-xero-preview__meta-row">
                    <span className="invoice-xero-preview__meta-label">ABN</span>
                    <PreviewInlineEditable
                      value=""
                      editable
                      readOnly={previewUiReadOnly}
                      className="invoice-xero-preview__meta-value invoice-xero-preview__meta-value--inline"
                      onSave={onSaveAbn}
                    />
                  </div>
                ) : null
              ) : null}
            </div>
          ) : (
            <PartyColumnPlaceholder />
          )}
        </div>
        ) : null}

        <table className="invoice-xero-preview__lines">
          <thead>
            <tr>
              <th>Description</th>
              <th className="text-end">
                {useCommissionLineLayout ? "Quantity" : "Qty."}
              </th>
              <th className="text-end">
                {useCommissionLineLayout ? "Unit Price" : "Unit price"}
              </th>
              {showLineDiscount ? <th className="text-end">Discount</th> : null}
              <th className="text-end">
                {useCommissionLineLayout ? "GST" : "Tax rate"}
              </th>
              <th className="text-end">Amount {currency}</th>
            </tr>
          </thead>

          <tbody>
            {visibleLines.length ? (
              visibleLines.map((row, index) => (
                <tr key={row.id || index}>
                  <td className="invoice-xero-preview__line-desc-cell">
                    <PreviewLineDescription row={row} />
                  </td>

                  <td className="text-end">{row.quantity ?? "—"}</td>

                  <td className="text-end">
                    {formatInvoiceMoney(row.unit_price, "", { decimals: 2 })}
                  </td>

                  {showLineDiscount ? (
                    <td className="text-end">
                      {formatInvoiceMoney(row.discount, "", { decimals: 2 })}
                    </td>
                  ) : null}

                  <td className="text-end">
                    {useExTaxLineLayout
                      ? formatCommissionPreviewGstRate(row)
                      : formatLineItemTaxRateDisplay(row.tax_rate, row.tax_type)}
                  </td>

                  <td className="text-end">
                    {(() => {
                      const lineAmount = useExTaxLineLayout
                        ? getLineDisplayTotalAmount(row)
                        : buildComputedLineItem(row, row.tax_type).amount;

                      return formatInvoiceMoney(lineAmount, "", {
                        decimals: 2,
                      });
                    })()}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={showLineDiscount ? 6 : 5}
                  className="text-center text-muted py-4"
                >
                  No line items added yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="invoice-xero-preview__totals-wrap invoice-pdf-avoid-break">
          <div className="invoice-xero-preview__totals">
            <PreviewTotalRow
              label="Subtotal"
              value={formatAmount(displaySubtotal)}
            />

            {showDiscountTotal && hasDiscount && (
              <PreviewTotalRow
                label="Discount"
                value={`-${formatAmount(displayDiscount)}`}
                variant="discount"
              />
            )}

            {taxLabel && displayTaxAmount > 0 && (
              <PreviewTotalRow
                label={taxLabel}
                value={formatAmount(displayTaxAmount)}
                variant="tax"
              />
            )}

            <PreviewTotalRow
              label={`TOTAL ${currency}`}
              value={formatAmount(displayTotal)}
              variant="grand"
              showDividerBefore
            />

            {previewPaidTotal > 0 && !draft?.hideLessPaid ? (
              <PreviewLessPaidRow
                paymentDate={paymentDate}
                value={formatAmount(previewPaidTotal)}
              />
            ) : null}

            <PreviewTotalRow
              label={`AMOUNT DUE ${currency}`}
              value={formatAmount(displayAmountDue)}
              variant="due"
              showDividerBefore={previewPaidTotal > 0}
            />
          </div>
        </div>

        {((branding?.payment_advice &&
          displayOpts.footer.showPaymentAdvice !== false) ||
          (branding?.terms_conditions &&
            displayOpts.footer.showTerms !== false)) && (
          <div className="invoice-xero-preview__footer invoice-pdf-avoid-break">
            <div className="invoice-xero-preview__footer-grid">
              {branding?.payment_advice &&
              displayOpts.footer.showPaymentAdvice !== false ? (
                <div className="invoice-xero-preview__footer-section invoice-pdf-avoid-break">
                  <div className="invoice-xero-preview__footer-heading">
                    Payment Advice
                  </div>

                  <PaymentAdviceSection
                    content={branding.payment_advice}
                    footerOptions={displayOpts.footer}
                  />
                </div>
              ) : null}

              {branding?.terms_conditions &&
              displayOpts.footer.showTerms !== false ? (
                <div className="invoice-xero-preview__footer-section invoice-pdf-avoid-break">
                  <div className="invoice-xero-preview__footer-heading">
                    Payment Terms and Conditions:
                  </div>

                  <TermsConditionsSection content={branding.terms_conditions} />
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (embedded) {
    return sheet;
  }

  return (
    <div className="invoice-xero-preview">
      {showToolbar ? (
        <div className="invoice-xero-preview__toolbar no-print">
          <div>
            <h6 className="mb-0 fw-bold">Invoice preview</h6>

            <div className="text-muted small">
              Preview how this document will look before saving.
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-light btn-sm d-inline-flex align-items-center gap-2"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              <FaDownload size={13} />

              {downloadingPdf ? "Downloading…" : "Download PDF"}
            </button>

            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={onClose}
            >
              Close preview
            </button>
          </div>
        </div>
      ) : null}

      <div
        className="invoice-xero-preview__canvas"
        id="invoice-xero-preview-print"
      >
        {sheet}
      </div>
    </div>
  );
}

export default InvoiceXeroPreview;
